import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { Notification } from '../types'

/**
 * Real-time notification hook.
 * Loads recent notifications from DB and subscribes to inserts via Supabase Realtime.
 */
export function useNotifications(orgId: string | null, userId: string | null) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)

  // Load recent notifications
  const load = useCallback(async () => {
    if (!orgId) return
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(50)

    // Include org-wide (user_id IS NULL) + personal notifications
    if (userId) {
      query = query.or(`user_id.is.null,user_id.eq.${userId}`)
    } else {
      query = query.is('user_id', null)
    }

    const { data } = await query
    if (data) {
      setNotifications(data as Notification[])
      setUnreadCount((data as Notification[]).filter(n => !n.read_at).length)
    }
  }, [orgId, userId])

  useEffect(() => { load() }, [load])

  // Subscribe to new notifications via Supabase Realtime
  useEffect(() => {
    if (!orgId) return

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `org_id=eq.${orgId}`,
        },
        (payload) => {
          const newNotif = payload.new as Notification
          // Only add if it's for this user or org-wide
          if (newNotif.user_id && newNotif.user_id !== userId) return
          setNotifications(prev => [newNotif, ...prev].slice(0, 50))
          setUnreadCount(prev => prev + 1)
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [orgId, userId])

  const markRead = useCallback(async (id: string) => {
    await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)

    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n),
    )
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const markAllRead = useCallback(async () => {
    if (!orgId) return
    const unreadIds = notifications.filter(n => !n.read_at).map(n => n.id)
    if (unreadIds.length === 0) return

    await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', unreadIds)

    setNotifications(prev =>
      prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
    )
    setUnreadCount(0)
  }, [orgId, notifications])

  return { notifications, unreadCount, markRead, markAllRead, reload: load }
}
